'use client';

import clsx from 'clsx';
import { useTranslations } from 'next-intl';

import Link from '@commons/components/Link';
import linkStyles from '@commons/components/Link/index.module.scss';
import Text from '@commons/components/Text';
import Flex from '@commons/containers/Flex';
import type { DrupalMenuLink } from '@commons/services/menu';

import styles from './index.module.scss';

// Type definition for the Footer Menu component properties.

type Props = {
  menu: DrupalMenuLink[];
};

/**
 *  Footer Copyright component.
 */

const FooterCopyright = ({ menu = [] }: Props) => {
  const t = useTranslations('footer');

  const hasMenu = menu.length > 0;
  if (!hasMenu) return null;

  const renderedLinks = menu.map((link) => {
    const { title, url, uuid } = link;
    const className = clsx(styles.copyRightLink, link.class);

    if (className.split(/\s+/).includes('ot-sdk-show-settings')) {
      return <CookieSettingsButton title={title} className={className} key={uuid} />;
    }

    return (
      <Link transform="uppercase" href={url} level="body-xs" className={className} key={uuid}>
        {title}
      </Link>
    );
  });

  return (
    <Flex
      gap={{ xs: 'xl', lg: 'sm' }}
      align={{ xs: 'start', xl: 'center' }}
      justify="start"
      direction={{ xs: 'column-reverse', lg: 'row' }}
    >
      <Text
        as="p"
        transform="uppercase"
        color="grey-4"
        level="body-xs"
        marginBottom="none"
        className={styles.copyrightText}
      >
        © {new Date().getFullYear()} AiiA. {t('copyright')}
      </Text>
      <Flex
        gap={{ xs: 'xs', lg: 'sm' }}
        align={{ xs: 'start', xl: 'center' }}
        justify="start"
        direction={{ xs: 'column', sm: 'row' }}
        className={styles.copyrightMenu}
        wrap="wrap"
      >
        {renderedLinks}
      </Flex>
    </Flex>
  );
};

type CookieSettingsButtonProps = {
  title: string;
  className: string;
};

// CookiePro settings button
const CookieSettingsButton = ({ title, className }: CookieSettingsButtonProps) => (
  <button type="button" className={clsx('link', linkStyles.link, styles.cookieSettings, className)}>
    <Text
      as="span"
      level="body-xs"
      transform="uppercase"
      className={linkStyles.label}
      marginBottom="none"
    >
      {title}
    </Text>
  </button>
);

export default FooterCopyright;
